/** A limit and what is spent against it. */

import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/cn";

import { mergeStyle } from "../internal/style";
import "./QuotaMeter.css";

export interface QuotaMeterProps {
  readonly label: ReactNode;
  readonly used: number;
  readonly limit: number;
  /** Shown once the limit is reached. */
  readonly exhaustedHint?: ReactNode;
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function QuotaMeter({ label, used, limit, exhaustedHint, className, style }: QuotaMeterProps) {
  const share = limit <= 0 ? 0 : Math.min(1, Math.max(0, used / limit));
  const exhausted = limit > 0 && used >= limit;

  return (
    <div
      className={cn("al-quota", exhausted ? "al-quota-exhausted" : undefined, className)}
      style={style}
    >
      <div className="al-quota-head">
        <span className="al-quota-label">{label}</span>
        <span className="al-quota-count al-tabular">
          {used} of {limit}
        </span>
      </div>
      <div
        className="al-quota-track"
        role="meter"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={limit}
      >
        <div className="al-quota-fill" style={mergeStyle(undefined, { width: `${share * 100}%` })} />
      </div>
      {exhausted && exhaustedHint !== undefined ? (
        <div className="al-quota-hint">{exhaustedHint}</div>
      ) : null}
    </div>
  );
}
